import React, { useState } from 'react'
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  Button,
  FormLabel,
  FieldError,
  TextArea,
  SelectableItemList,
} from '@pandiafi/design-system'
import {
  Block,
  BlockRow,
  ContextNotification,
  Modal,
} from '@pandiafi/components'

import type { ParkingSpotEquipment } from '../../../common/types'
import { equipmentOptions } from './uiSelectors'
import { JoinQueueBody } from '../../../common/validations/parkingSpotSchemas'
import type { CoreParkingSpot } from '../../../common/validations/contractParkingSpotsSchema'
import {
  getExistingQueueEquipmentTypes,
  getJoinableQueueEquipmentTypes,
  hasJoinableQueueSelectionRefinement,
  isJoinableQueueSelection,
  type QueueItem,
} from './parkingSpots.helpers'

import { useAPIJoinParkingQueue } from 'api/apiHooks/useAPIJoinParkingQueue'

type Option = { value: ParkingSpotEquipment; label: string }

interface JoinQueueFormProps {
  show: boolean
  onClose: () => void
  costPoolId: number
  parkingSpots: ReadonlyArray<CoreParkingSpot>
  queueItems: ReadonlyArray<QueueItem>
  userMetadata: {
    isImpersonator: boolean
  }
}

const INITIAL_VALUES: JoinQueueBody = {
  equipmentWishes: [],
  additionalInformation: '',
  costPoolId: 0,
}

export const JoinQueueForm: React.FC<JoinQueueFormProps> = ({
  show,
  onClose,
  costPoolId,
  parkingSpots,
  queueItems,
  userMetadata,
}) => {
  const { t } = useTranslation('parking')
  const join = useAPIJoinParkingQueue()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleValidate = (values: JoinQueueBody) => {
    const errors: Partial<Record<keyof JoinQueueBody, string>> = {}
    const isSelectionJoinable = isJoinableQueueSelection(
      parkingSpots,
      queueItems,
      values.equipmentWishes
    )
    const hasSelectionRefinement = hasJoinableQueueSelectionRefinement(
      parkingSpots,
      queueItems,
      values.equipmentWishes
    )

    if (!show) return errors
    if (!values.equipmentWishes || values.equipmentWishes.length === 0) {
      errors.equipmentWishes = t('errors.equipmentRequired')
    } else if (!isSelectionJoinable && !hasSelectionRefinement) {
      errors.equipmentWishes = t('errors.equipmentUnavailable')
    }
    return errors
  }

  const closeAndReset = (form: FormikHelpers<JoinQueueBody>) => {
    form.setErrors({})
    form.resetForm()
    setSubmitError(null)
    onClose()
  }

  const handleOnSubmit = async (
    values: JoinQueueBody,
    form: FormikHelpers<JoinQueueBody>
  ) => {
    setSubmitError(null)
    try {
      const uniqueWishes = Array.from(
        new Set(values.equipmentWishes)
      ) as ParkingSpotEquipment[]
      await join.mutateAsync({
        equipmentWishes: uniqueWishes,
        additionalInformation: values.additionalInformation.trim(),
        costPoolId,
      })
      closeAndReset(form)
    } catch (_err) {
      setSubmitError(t('errors.joinFailed'))
    } finally {
      form.setSubmitting(false)
    }
  }

  const handleOnCancel = (form: FormikHelpers<JoinQueueBody>) => {
    closeAndReset(form)
  }

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      onSubmit={handleOnSubmit}
      validate={handleValidate}
      initialErrors={handleValidate(INITIAL_VALUES)}
      enableReinitialize={false}
      validateOnBlur
    >
      {(form) => {
        const isSelectionJoinable = isJoinableQueueSelection(
          parkingSpots,
          queueItems,
          form.values.equipmentWishes
        )
        const allOpts: Option[] = equipmentOptions(
          t,
          getExistingQueueEquipmentTypes(parkingSpots)
        ).map((o) => ({ value: o.value, label: o.label }))

        const compatibleEquipment = getJoinableQueueEquipmentTypes(
          parkingSpots,
          queueItems,
          form.values.equipmentWishes
        )

        const listItems = (selected: ParkingSpotEquipment[]) =>
          allOpts.map((opt) => {
            const isCompatible = compatibleEquipment.includes(opt.value)
            const isSelected = selected.includes(opt.value)
            const isDisabled =
              join.isPending ||
              form.isSubmitting ||
              (!isCompatible && !isSelected)
            return {
              text: opt.label,
              selected: isSelected,
              disabled: isDisabled,
              showIcon: false,
              onClick: () => {
                if (join.isPending || form.isSubmitting) return
                if (!isCompatible && !isSelected) return
                const next = isSelected
                  ? selected.filter((v) => v !== opt.value)
                  : [...selected, opt.value]
                form.setFieldTouched('equipmentWishes', true, false)
                form.setFieldValue('equipmentWishes', Array.from(new Set(next)))
              },
            }
          })

        const canSave =
          isSelectionJoinable &&
          form.dirty &&
          !form.isSubmitting &&
          !join.isPending

        return (
          <Modal
            show={show}
            onClose={() => handleOnCancel(form)}
            title={t('queue.join')}
          >
            <Modal.Content>
              <Form>
                <Block>
                  <BlockRow>
                    <FormLabel required>
                      {t('form.fields.equipment.label')}
                    </FormLabel>
                    <Field name="equipmentWishes">
                      {({
                        field,
                        meta,
                      }: FieldProps<JoinQueueBody['equipmentWishes']>) => (
                        <div className="w-full">
                          <SelectableItemList
                            items={listItems(field.value || [])}
                          />
                          {meta.touched && meta.error && (
                            <FieldError error={meta.error} />
                          )}
                        </div>
                      )}
                    </Field>
                  </BlockRow>
                  <BlockRow>
                    <FormLabel>
                      {t('form.fields.additionalInfo.label')}
                    </FormLabel>
                    <Field name="additionalInformation">
                      {({
                        field,
                        meta,
                      }: FieldProps<
                        JoinQueueBody['additionalInformation']
                      >) => (
                        <div className="w-full">
                          <TextArea
                            {...field}
                            rows={3}
                            maxLength={1000}
                            placeholder={t(
                              'form.fields.additionalInfo.placeholder'
                            )}
                            disabled={join.isPending || form.isSubmitting}
                          />
                          {meta.touched && meta.error && (
                            <FieldError error={meta.error} />
                          )}
                        </div>
                      )}
                    </Field>
                  </BlockRow>
                  {submitError && (
                    <BlockRow>
                      <ContextNotification type="error">
                        {submitError}
                      </ContextNotification>
                    </BlockRow>
                  )}
                </Block>
              </Form>
            </Modal.Content>

            <Modal.Buttons>
              <Button variant="cancel" onClick={() => handleOnCancel(form)}>
                {t('form.actions.cancel')}
              </Button>
              <Button
                onClick={() => form.submitForm()}
                disabled={!canSave || userMetadata.isImpersonator}
              >
                {t('form.actions.join')}
              </Button>
            </Modal.Buttons>
          </Modal>
        )
      }}
    </Formik>
  )
}
